import React, { ForwardedRef, forwardRef, ReactElement, useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"
import { useWindowSize } from "../../../hooks"
import { theme } from "../../../theme"
import { RadioCard } from "../../atoms/controls"
import { Heading2 } from "../../atoms/text"
import { RadioCardOption } from "./RadioCardOption"
import { FocusableDiv } from "../../common/FocusableDiv"

export enum RadioCardGroupFlow {
  Row = "row",
  Column = "column",
}

export type RadioCardGroupProps<TOptions extends RadioCardOption[]> = {
  value: TOptions[number]["value"] | undefined
  onChange: (value: TOptions[number]["value"]) => void
  options: TOptions
  name?: string
  className?: string
  disabled?: boolean
  errorMessage?: string
  title?: string
  required?: boolean
  flow?: RadioCardGroupFlow
  loading?: boolean
  dataCy?: string
}

const RadioCardGroupInner = <
  TOptions extends {
    value: string
    label?: string
    imageUrl?: string
    icon?: ReactElement
  }[]
>(
  props: RadioCardGroupProps<TOptions>,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const {
    value,
    options,
    name = "",
    className,
    disabled,
    errorMessage,
    title,
    flow = RadioCardGroupFlow.Column,
    loading,
    dataCy,
  } = props

  const [isAutoFill, setIsAutoFill] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [windowWidth] = useWindowSize()
  const fromWidth = 700
  const toWidth = 742

  useEffect(() => {
    if (
      containerRef?.current &&
      containerRef.current.offsetWidth > fromWidth &&
      containerRef.current.offsetWidth < toWidth &&
      options.length >= 3 &&
      flow === RadioCardGroupFlow.Column
    ) {
      setIsAutoFill(true)
      return
    }

    setIsAutoFill(false)
  }, [windowWidth, containerRef?.current?.offsetWidth])

  const onChange = (newValue: string) => {
    props.onChange(newValue)
  }

  return (
    <FocusableDiv ref={ref} className={className}>
      <Root ref={containerRef}>
        {!!title && (
          <Title>
            {title}
            {props.required ? "*" : ""}
          </Title>
        )}

        {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        <CardGroup $flow={flow} $isAutoFill={isAutoFill} data-cy={dataCy}>
          {loading && <RadioCard name="" loading={loading} />}
          {!loading &&
            options.map((option) => (
              <RadioCard
                imageUrl={option.imageUrl}
                icon={option.icon}
                key={option.value}
                name={name ? `${name}_${option.value}` : option.value}
                label={option.label}
                checked={option.value === value}
                onChange={() => onChange(option.value)}
                hasError={!!errorMessage}
                disabled={disabled}
              />
            ))}
        </CardGroup>
      </Root>
    </FocusableDiv>
  )
}

export const RadioCardGroup = forwardRef(RadioCardGroupInner)

const Root = styled(FocusableDiv)`
  display: flex;
  flex-direction: column;
`

const Title = styled(Heading2)`
  margin-bottom: ${theme.spacing.lx};
`

const ErrorMessage = styled.div`
  color: ${theme.color.error};
  margin-bottom: ${theme.spacing.l};
  font-size: ${theme.fontSize.s};
  line-height: ${theme.lineHeight.s};
`

const CardGroup = styled.div<{ $flow: RadioCardGroupFlow; $isAutoFill: boolean }>`
  display: grid;
  grid-gap: ${theme.spacing.m};
  width: 100%;

  ${({ $flow }) =>
    $flow === RadioCardGroupFlow.Row &&
    css`
      grid-auto-flow: row;
      width: 242px;
    `}

  ${({ $flow, $isAutoFill }) =>
    $flow === RadioCardGroupFlow.Column &&
    css`
      grid-template-columns: ${$isAutoFill === true ? "repeat(3, 1fr)" : "repeat(3, 242px)"};
    `}
`
